import { useEffect, useState } from 'react';
import Layout from '../components/layout';
import Title from '../components/title';
import Statscard from '../components/statscard';
import './dashboard.css';
import { supabase } from '../supabase';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import calendarIcon from '../assets/calendar.svg';
import ticketIcon from '../assets/ticket.svg';
import usersIcon from '../assets/users.svg';
import moneyIcon from '../assets/money.svg';
import ratingIcon from '../assets/rating.svg';

const Dashboard = () => {
  const [events, setEvents] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);

  const fetchDashboardData = async () => {
    const eventsRes = await supabase
      .from('Events')
      .select(`
        id,
        title,
        created_at,
        category_id,
        price,
        Categories(name)
      `);

    const bookingsRes = await supabase
      .from('Bookings')
      .select(`
        id,
        created_at,
        event_id,
        status,
        total_price,
        number_of_tickets,
        Events(title),
        Users(name)
      `);

    const usersRes = await supabase
      .from('Users')
      .select('id, created_at');

    setEvents(eventsRes.data || []);
    setBookings(bookingsRes.data || []);
    setUsers(usersRes.data || []);
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const percentageChange = (current, previous) => {
    const change = ((current - previous) / previous) * 100;
    const sign = change >= 0 ? '+' : '';
    return Number.isFinite(change) ? `${sign}${change.toFixed(1)}%` : '+0.0%';
  };

  const currentMonthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1);

  const monthlyData = [];
  for (let offset = 5; offset >= 0; offset--) {
    const date = new Date(currentMonthStart);
    date.setMonth(currentMonthStart.getMonth() - offset);

    monthlyData.push({
      key: `${date.getFullYear()}-${date.getMonth()}`,
      month: date.toLocaleString('en-US', { month: 'short' }),
      revenue: 0,
      bookings: 0,
      users: 0,
    });
  }

  bookings.forEach((booking) => {
    const date = new Date(booking.created_at);
    const key = `${date.getFullYear()}-${date.getMonth()}`;
    const monthItem = monthlyData.find((item) => item.key === key);

    if (monthItem) {
      monthItem.bookings += 1;
      monthItem.revenue += Number(booking.total_price) || 0;
    }
  });

  users.forEach((user) => {
    const date = new Date(user.created_at);
    const key = `${date.getFullYear()}-${date.getMonth()}`;
    const monthItem = monthlyData.find((item) => item.key === key);

    if (monthItem) {
      monthItem.users += 1;
    }
  });

  const categoryMap = {};
  events.forEach((event) => {
    const categoryName = event.Categories?.name;
    if (categoryName) {
      categoryMap[categoryName] = (categoryMap[categoryName] || 0) + 1;
    }
  });

  const chartColors = ['#9bef00', '#57dc00', '#14b300', '#84c800', '#3b8f00', '#2f6f00'];

  const categoryData = Object.entries(categoryMap)
    .map(([name, value], index) => ({
      name,
      value,
      fill: chartColors[index % chartColors.length],
    }))
    .sort((a, b) => b.value - a.value);

  const recentBookings = [...bookings]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 6);

  const eventBookingMap = {};
  bookings.forEach((booking) => {
    const eventName = booking.Events?.title;
    if (eventName) {
      eventBookingMap[eventName] = (eventBookingMap[eventName] || 0) + 1;
    }
  });

  const topEvents = Object.entries(eventBookingMap)
    .map(([name, bookingCount]) => ({
      name,
      bookingCount,
    }))
    .sort((a, b) => b.bookingCount - a.bookingCount)
    .slice(0, 5);

  const totalRevenue = bookings.reduce((sum, booking) => {
    return sum + (Number(booking.total_price) || 0);
  }, 0);

  const avgBookingValue = bookings.length ? totalRevenue / bookings.length : 0;

  const currentMonth = monthlyData[monthlyData.length - 1] || { bookings: 0, revenue: 0, users: 0 };
  const previousMonth = monthlyData[monthlyData.length - 2] || { bookings: 0, revenue: 0, users: 0 };

  const stats = [
    {
      label: 'Total Events',
      value: events.length.toLocaleString(),
      change: '',
      icon: calendarIcon,
    },
    {
      label: 'Total Bookings',
      value: bookings.length.toLocaleString(),
      change: percentageChange(currentMonth.bookings, previousMonth.bookings),
      icon: ticketIcon,
    },
    {
      label: 'Total Users',
      value: users.length.toLocaleString(),
      change: percentageChange(currentMonth.users, previousMonth.users),
      icon: usersIcon,
    },
    {
      label: 'Revenue',
      value: `$${totalRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
      change: percentageChange(currentMonth.revenue, previousMonth.revenue),
      icon: moneyIcon,
    },
    {
      label: 'Avg Booking Value',
      value: `$${avgBookingValue.toFixed(2)}`,
      change: '',
      icon: ratingIcon,
    },
  ];

  return (
    <Layout>
      <div className="dashboard_page">
        <Title
          subtitle="Welcome back! Here's what's happening with Eventra today."
          title="DASHBOARD OVERVIEW"
        />

        <div className="dashboard_stats">
          {stats.map((stat) => (
            <Statscard
              key={stat.label}
              change={stat.change}
              icon={stat.icon}
              label={stat.label}
              value={stat.value}
            />
          ))}
        </div>

        <section className="dashboard_analytics_card">
          <div className="dashboard_section_head">
            <div className="dashboard_card_title">ANALYTICS OVERVIEW</div>
            <div className="dashboard_card_count">Last 6 Months</div>
          </div>

          <div className="dashboard_chart_title">Revenue Analytics</div>
          <div className="dashboard_chart_subtitle">Monthly revenue trends</div>

          <div className="dashboard_chart_box dashboard_chart_box_lg">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthlyData}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#9bef00" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#9bef00" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Area
                  dataKey="revenue"
                  name="Revenue"
                  stroke="#9bef00"
                  strokeWidth={3}
                  fill="url(#colorRevenue)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="dashboard_row_two">
          <div className="dashboard_analytics_card">
            <div className="dashboard_chart_title">Bookings Trend</div>
            <div className="dashboard_chart_subtitle">Monthly booking statistics</div>

            <div className="dashboard_chart_box dashboard_chart_box_md">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyData}>
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Bar
                    dataKey="bookings"
                    name="Bookings"
                    fill="#9bef00"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="dashboard_analytics_card">
            <div className="dashboard_chart_title">Events by Category</div>
            <div className="dashboard_chart_subtitle">Category distribution</div>

            <div className="dashboard_chart_box dashboard_chart_box_md">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categoryData}
                    dataKey="value"
                    cx="50%"
                    cy="50%"
                    innerRadius={52}
                    outerRadius={82}
                    paddingAngle={3}
                  />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="dashboard_category_legend">
              {categoryData.slice(0, 5).map((category) => (
                <span key={category.name}>
                  <i style={{ backgroundColor: category.fill }} />
                  {category.name} ({category.value})
                </span>
              ))}
            </div>
          </div>
        </section>

        <section className="dashboard_analytics_card">
          <div className="dashboard_chart_title">User Growth</div>
          <div className="dashboard_chart_subtitle">New user registrations over time</div>

          <div className="dashboard_chart_box dashboard_chart_box_md">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthlyData}>
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Line
                  dataKey="users"
                  name="Users"
                  stroke="#9bef00"
                  strokeWidth={3}
                  dot={{ fill: '#9bef00', r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="dashboard_row_two">
          <div className="dashboard_analytics_card dashboard_list_card">
            <div className="dashboard_list_head">
              <div className="dashboard_chart_title">Recent Bookings</div>
            </div>

            <div className="dashboard_chart_subtitle">Latest booking activities</div>

            <div className="dashboard_list_items">
              {recentBookings.map((booking) => (
                <div className="dashboard_list_item" key={booking.id}>
                  <div>
                    <strong>{booking.Users?.name}</strong>
                    <p>{booking.Events?.title}</p>
                    <small>{new Date(booking.created_at).toLocaleDateString()}</small>
                  </div>

                  <span className={`dashboard_badge ${booking.status?.toLowerCase()}`}>
                    {booking.status}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="dashboard_analytics_card dashboard_list_card">
            <div className="dashboard_list_head">
              <div className="dashboard_chart_title">Top Events</div>
            </div>

            <div className="dashboard_chart_subtitle">Most booked events this period</div>

            <div className="dashboard_list_items">
              {topEvents.map((event) => (
                <div className="dashboard_list_item" key={event.name}>
                  <div>
                    <strong>{event.name}</strong>
                    <p>High demand event</p>
                  </div>

                  <span className="dashboard_count">{event.bookingCount} bookings</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default Dashboard;
